/**
 * Pure helpers for the foreground notification banner.
 *
 * Kept side-effect-free so they are trivially unit-testable without
 * any React / Expo / native dependencies.
 */

import {
  GAME_TOP_BAR_HEIGHT,
  HOME_HEADER_HEIGHT,
  LEADERBOARD_HEADER_HEIGHT,
  STANDARD_HEADER_HEIGHT,
} from './gameChrome'

/** Turn banners are useful only while the turn change is still recent. */
export const MAX_TURN_BANNER_AGE_MS = 10 * 60 * 1000
const MAX_TURN_BANNER_FUTURE_SKEW_MS = 30 * 1000

/** Regex that matches the push `data.url` produced by the API push helpers. */
const GAME_URL_RE = /^\/games\/([^/]+)/

/**
 * Parse a gameId out of a push notification `data.url` string.
 *
 * Returns the raw id string when the URL matches `/games/<id>`, or
 * `null` when the URL is absent / does not target a game screen.
 *
 * @example
 *   parseGameIdFromPushUrl('/games/abc-123')  // → 'abc-123'
 *   parseGameIdFromPushUrl('/profile')         // → null
 *   parseGameIdFromPushUrl(undefined)          // → null
 */
export function parseGameIdFromPushUrl(url: string | undefined): string | null {
  if (!url) return null
  const m = GAME_URL_RE.exec(url)
  return m ? (m[1] ?? null) : null
}

/**
 * The private websocket event delivered to the player whose turn just began.
 * Kept as a tiny local bus because the game socket lives in the screen tree
 * while the banner lives at the root of the navigation tree.
 */
export interface YourTurnSocketEvent {
  gameId: string
  gameName: string
  turn: number | null
  occurredAt: number
}

type YourTurnListener = (event: YourTurnSocketEvent) => void
const yourTurnListeners = new Set<YourTurnListener>()

export function subscribeToYourTurn(listener: YourTurnListener): () => void {
  yourTurnListeners.add(listener)
  return () => yourTurnListeners.delete(listener)
}

export function publishYourTurn(event: YourTurnSocketEvent): void {
  for (const listener of yourTurnListeners) listener(event)
}

export interface NotificationTopBarPlacement {
  top: number
  left: number
  right: number
  height: number
}

const HOME_PATH_RE = /^(?:\/\(app\))?(?:\/(?:index)?)?$/
const GAME_TOP_BAR_PATH_RE = /^(?:\/\(app\))?\/games\/[^/]+(?:\/(?:chat|log))?$/
const LEADERBOARD_PATH_RE = /^(?:\/\(app\))?\/(?:hof(?:\/win-rate)?|leaderboard)$/

/**
 * Resolve the height of the current screen's top chrome. Most app headers are
 * one-line 48px rows; the branded home and Hall of Fame
 * headers are taller, while the active game and its chat/log surfaces use the
 * compact 46px game bar.
 */
export function topBarHeightForPath(pathname: string): number {
  if (HOME_PATH_RE.test(pathname)) return HOME_HEADER_HEIGHT
  if (GAME_TOP_BAR_PATH_RE.test(pathname)) return GAME_TOP_BAR_HEIGHT
  if (LEADERBOARD_PATH_RE.test(pathname)) return LEADERBOARD_HEADER_HEIGHT
  return STANDARD_HEADER_HEIGHT
}

/**
 * Return whether a turn event is recent enough to interrupt the foreground
 * UI. Missing/invalid timestamps are rejected so a stale queued event from an
 * older app session cannot appear as if it just happened.
 */
export function isRecentTurnBanner(
  occurredAt: number | string | null | undefined,
  now = Date.now(),
): boolean {
  const timestamp = typeof occurredAt === 'string' ? Date.parse(occurredAt) : occurredAt
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return false
  const age = now - timestamp
  return age >= -MAX_TURN_BANNER_FUTURE_SKEW_MS && age <= MAX_TURN_BANNER_AGE_MS
}

/** Keep one visible banner while choosing the newest queued turn event. */
export function newestTurnBanner<T extends { occurredAt: number }>(
  current: T | null,
  next: T,
): T {
  return current && current.occurredAt >= next.occurredAt ? current : next
}

/**
 * Admit a turn event into the single foreground slot. An event for the game
 * the player is already viewing never shows: that game's own turn UI is on
 * screen. Otherwise the newest event wins, as in `newestTurnBanner`.
 */
export function admitTurnBanner<T extends { gameId: string; occurredAt: number }>(
  current: T | null,
  next: T,
  viewingGameId: string | null,
): T | null {
  if (viewingGameId && next.gameId === viewingGameId) return current
  return newestTurnBanner(current, next)
}

/**
 * Drop the visible or queued banner once the player enters its game, whether
 * they tapped the banner itself or opened the game another way (a games-list
 * row, a deep link). A banner for a different game is kept.
 */
export function clearTurnBannerForViewedGame<T extends { gameId: string }>(
  current: T | null,
  viewingGameId: string | null,
): T | null {
  if (!current || !viewingGameId) return current
  return current.gameId === viewingGameId ? null : current
}

/** The subset of an expo-notifications presented notification we inspect. */
export interface PresentedNotificationLike {
  request: { identifier: string; content: { data?: Record<string, unknown> | null } }
}

/**
 * Identifiers of the system-tray "your turn" notifications for one game. The
 * foreground handler hides the native banner but still lists turn pushes in
 * the tray, so entering the game clears its now-stale turn notifications.
 * Other kinds (direct messages, team chat, game over) are left alone.
 */
export function presentedTurnNotificationIdsForGame(
  presented: readonly PresentedNotificationLike[],
  gameId: string,
): string[] {
  if (!gameId) return []
  return presented
    .filter(({ request }) => {
      const data = request.content.data
      if (data?.kind !== 'YOUR_TURN') return false
      const url = typeof data.url === 'string' ? data.url : undefined
      const target = parseGameIdFromPushUrl(url) ?? (typeof data.gameId === 'string' ? data.gameId : null)
      return target === gameId
    })
    .map(({ request }) => request.identifier)
}

/**
 * Place the banner in the same full-width top-bar slot as GameBanner's
 * reconnecting state. RootLayout owns the banner, so it uses the safe-area
 * inset directly rather than depending on any screen's content layout.
 */
export function notificationTopBarPlacement(args: {
  pathname: string
  insetTop: number
  fontScale?: number
}): NotificationTopBarPlacement {
  const { pathname, insetTop } = args
  return {
    top: insetTop,
    left: 0,
    right: 0,
    // One title line (12), one body line (16), 2px gap and 4px padding.
    // Grow for large accessibility fonts instead of centering overflowing text.
    height: Math.max(topBarHeightForPath(pathname), Math.ceil(28 * (args.fontScale ?? 1) + 10)),
  }
}

/**
 * Resolve the target game route from a push notification's `data` payload,
 * for deep-linking when the user taps the notification.
 *
 * The API uses these payload shapes:
 *   - turn pushes:           `{ kind: 'YOUR_TURN', url: '/games/<id>', turn }`
 *   - game-finished/
 *     player-eliminated:     `{ kind, gameId: '<id>' }`
 *   - direct message:        `{ kind: 'dm', gameId: '<id>', dmFrom: '<userId>' }`
 *   - team chat message:     `{ kind: 'team-chat', gameId: '<id>', team: 'A' }`
 *
 * All carry the gameId; this normalises into the in-app route
 * `/(app)/games/<id>` — or, for a direct message, straight to that thread
 * `/(app)/games/<id>/dm/<userId>`, for team chat `/(app)/games/<id>/dm/team`
 * — (or `null` when the push doesn't target a
 * game).
 */
export function gameRouteFromPushData(
  data: Record<string, unknown> | undefined | null,
): string | null {
  if (data?.kind === 'SUPPORT_TICKET' && typeof data.url === 'string' && /^\/settings\/inbox\/[A-Za-z0-9_-]{1,128}$/.test(data.url)) {
    return `/(app)${data.url}`
  }
  if (!data) return null
  const direct = typeof data.gameId === 'string' ? data.gameId : null
  const fromUrl = parseGameIdFromPushUrl(
    typeof data.url === 'string' ? data.url : undefined,
  )
  const gameId = direct ?? fromUrl
  if (!gameId) return null
  // A DM push deep-links straight into the conversation with the sender.
  if (data.kind === 'dm' && typeof data.dmFrom === 'string' && data.dmFrom) {
    return `/(app)/games/${gameId}/dm/${data.dmFrom}`
  }
  // A team chat push opens the team's secret thread.
  if (data.kind === 'team-chat') return `/(app)/games/${gameId}/dm/team`
  return `/(app)/games/${gameId}`
}

/** Extracts the `<id>` from the `/games/<id>` segment of a pathname. */
const GAME_PATH_RE = /(?:^|\/)games\/([^/]+)/

export function gameIdFromPathname(pathname: string): string | null {
  if (!pathname) return null
  return GAME_PATH_RE.exec(pathname)?.[1] ?? null
}

/**
 * Determine whether the user is currently viewing the game identified by
 * `targetGameId`.
 *
 * Compares against the `pathname` string returned by expo-router's
 * `usePathname()`. IMPORTANT: `usePathname()` STRIPS route-group segments like
 * `(app)`, so at runtime the game screens report as:
 *
 *   /games/<id>            (index route — the main view)
 *   /games/<id>/chat       (sub-tabs)
 *   /games/<id>/log        (sub-tabs)
 *   …
 *
 * NOT `/(app)/games/<id>` (see lib/deepLinkHelpers, which documents the same
 * group-omission). The previous implementation prefix-matched
 * `/(app)/games/<id>`, which NEVER matched the real pathname — so the
 * suppression silently no-op'd and the in-app banner still fired for the game
 * you were already looking at. We now pull the `/games/<id>` segment out
 * regardless of any leading group prefix, so both the runtime form and the
 * historical group-prefixed form resolve correctly.
 *
 * @param pathname     expo-router `usePathname()` value
 * @param targetGameId the gameId we got from the push notification
 */
export function isViewingGame(pathname: string, targetGameId: string): boolean {
  if (!pathname || !targetGameId) return false
  return gameIdFromPathname(pathname) === targetGameId
}

/**
 * A game can become visible as "my turn" in the shared games query just after
 * its screen is closed. That is not a new notification: the player just saw
 * that game's turn UI. Keep the suppression short so a later real turn change
 * still produces a banner.
 */
export const JUST_EXITED_GAME_BANNER_SUPPRESSION_MS = 5_000

export function isRecentlyExitedGame(
  recentlyExited: { gameId: string; exitedAt: number } | null,
  gameId: string,
  now = Date.now(),
): boolean {
  if (!recentlyExited || recentlyExited.gameId !== gameId) return false
  const elapsed = now - recentlyExited.exitedAt
  return elapsed >= 0 && elapsed <= JUST_EXITED_GAME_BANNER_SUPPRESSION_MS
}

// ─── Turn-banner dedup ────────────────────────────────────────────────────────

/** All turn events already rendered for one game in this app session. */
export interface TurnBannerHistory {
  renderedTurns: ReadonlySet<number>
  lastBannerAt: number | null
  lastBannerWasUnknown: boolean
}

/**
 * Independent paths raise the in-app "your turn" banner — the private
 * websocket event, push-arrival listener, and reconciliation poll watcher —
 * and they can all fire for one
 * turn flip, seconds apart. The old dedup only compared against the banner
 * currently on screen, so once the first auto-dismissed the second raised a
 * duplicate. This window must outlive the banner itself.
 */
export const TURN_BANNER_COOLDOWN_MS = 45_000

/**
 * Pushes queued on the wire while the app was closed are delivered in a burst
 * right after launch; they describe turns that were already waiting when the
 * user opened the app (visible on the home list anyway), not news. Suppress
 * push-path banners this soon after startup — the poll watcher's baseline
 * covers the equivalent case on its path, and any genuinely NEW flip in this
 * window is picked up by the next poll tick.
 */
export const TURN_BANNER_STARTUP_GRACE_MS = 10_000

/**
 * Decide whether a "your turn" banner should show, deduping across both
 * paths. Pure: the caller keeps the per-game map and records the returned
 * history when `show` is true.
 *
 *  - Every known turn is retained, rather than only the latest turn. This
 *    prevents an old push/poll result from re-showing after a later turn.
 *  - Unknown legacy pushes use a short cooldown because they carry no event
 *    identifier. Current turn pushes include the turn number and are filtered
 *    by the caller so stale-turn reminders cannot become banners.
 *  - Unknown pushes are suppressed inside the startup grace window.
 */
export function decideTurnBanner(args: {
  history: TurnBannerHistory
  turn: number | null
  now: number
  appStartedAt: number
}): { show: boolean; history: TurnBannerHistory } {
  const { history, turn, now, appStartedAt } = args
  if (turn === null && now - appStartedAt < TURN_BANNER_STARTUP_GRACE_MS) {
    return { show: false, history }
  }
  if (turn !== null) {
    if (history.renderedTurns.has(turn)) return { show: false, history }
    if (
      history.lastBannerWasUnknown &&
      history.lastBannerAt !== null &&
      now - history.lastBannerAt < TURN_BANNER_COOLDOWN_MS
    ) {
      return { show: false, history }
    }
    const renderedTurns = new Set(history.renderedTurns)
    renderedTurns.add(turn)
    return {
      show: true,
      history: { renderedTurns, lastBannerAt: now, lastBannerWasUnknown: false },
    }
  }
  if (history.lastBannerAt !== null && now - history.lastBannerAt < TURN_BANNER_COOLDOWN_MS) {
    return { show: false, history }
  }
  return {
    show: true,
    history: { renderedTurns: history.renderedTurns, lastBannerAt: now, lastBannerWasUnknown: true },
  }
}
